// Service-worker lifecycle on the page (§11 M11, CODE_REVIEW S13/S14).
//
// · First install: the worker takes control (clients.claim) and adds COOP/COEP, but the page that is
//   already open is not cross-origin isolated yet. It reloads once to get multithreaded WASM — only while
//   no photo is open; otherwise the banner asks, so nobody loses an open folder (S14).
// · Update: the new worker WAITS (no automatic skipWaiting). The banner offers "Reload to update"; the click
//   saves pending sidecars, tells the waiting worker to activate, and reloads when it has. Activating under
//   a page still running the old bundle would delete the old hashed worker files it needs (S13).
import { create } from 'zustand';
import { flushSidecars } from '../state/persist';
import { useLibrary } from '../state/library';

type Reason = 'update' | 'isolation';
export const useSwUpdate = create<{ reason: Reason | null; apply: () => void }>(() => ({
  reason: null,
  apply: () => {},
}));

let reloading = false;
function reloadOnce() {
  if (reloading) return;
  reloading = true;
  location.reload();
}

/** True when reloading now would throw away what the user is doing. */
const busy = () => useLibrary.getState().photos.length > 0;

export function registerServiceWorker() {
  if (!('serviceWorker' in navigator)) return;
  let wantUpdate = false;
  navigator.serviceWorker.addEventListener('controllerchange', () => {
    if (wantUpdate) return reloadOnce();
    if (window.crossOriginIsolated) return;
    try {
      if (sessionStorage.getItem('epb.coiReload')) return;
      sessionStorage.setItem('epb.coiReload', '1');
    } catch {
      return;
    }
    if (!busy()) return reloadOnce();
    useSwUpdate.setState({
      reason: 'isolation',
      apply: () => void flushSidecars().then(reloadOnce, reloadOnce),
    });
  });
  void navigator.serviceWorker
    .register('/sw.js')
    .then((reg) => {
      const offer = (w: ServiceWorker) =>
        useSwUpdate.setState({
          reason: 'update',
          apply: () => {
            wantUpdate = true;
            void flushSidecars().finally(() => w.postMessage('SKIP_WAITING'));
          },
        });
      // Only an UPDATE waits: on first install there is no controller yet.
      if (reg.waiting && navigator.serviceWorker.controller) offer(reg.waiting);
      reg.addEventListener('updatefound', () => {
        const w = reg.installing;
        w?.addEventListener('statechange', () => {
          if (w.state === 'installed' && navigator.serviceWorker.controller) offer(w);
        });
      });
    })
    .catch(() => {
      /* no offline mode; the app still works online */
    });
}
